import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Plus, FileText, RefreshCw } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function VendorCredits() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [credits, setCredits] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [newCredit, setNewCredit] = useState({
    vendor_id: '', credit_date: new Date().toISOString().split('T')[0], reason: '',
    items: [{ description: 'Credit adjustment', quantity: 1, unit_price: 0 }]
  });

  useEffect(() => { fetchData(); }, [token]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [credRes, vendRes] = await Promise.all([
        fetch(`${API}/api/purchases/vendor-credits`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API}/api/purchases/vendors`, { headers: { 'Authorization': `Bearer ${token}` } })
      ]);
      if (credRes.ok) setCredits((await credRes.json()).vendor_credits || []);
      if (vendRes.ok) setVendors((await vendRes.json()).vendors || []);
    } catch (error) { console.error(error); }
    finally { setLoading(false); }
  };

  const createCredit = async () => {
    if (!newCredit.vendor_id || !newCredit.reason || !newCredit.items[0].unit_price) { toast.error('Fill required fields'); return; }
    setProcessing(true);
    try {
      const res = await fetch(`${API}/api/purchases/vendor-credits`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(newCredit)
      });
      if (res.ok) { toast.success('Vendor credit created'); setShowNew(false); fetchData(); }
      else { const err = await res.json(); toast.error(err.detail || 'Failed'); }
    } catch (error) { toast.error('Failed'); }
    finally { setProcessing(false); }
  };

  const formatCurrency = (amt) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 0 }).format(amt || 0);

  if (loading) return <div className="flex justify-center p-8"><RefreshCw className="h-6 w-6 animate-spin" /></div>;

  return (
    <Card className="mt-4">
      <CardHeader>
        <div className="flex justify-between items-center">
          <CardTitle className="flex items-center gap-2"><FileText className="h-5 w-5" />Vendor Credits</CardTitle>
          <Dialog open={showNew} onOpenChange={setShowNew}>
            <DialogTrigger asChild><Button><Plus className="h-4 w-4 mr-2" />New Credit</Button></DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader><DialogTitle>Create Vendor Credit</DialogTitle></DialogHeader>
              <div className="space-y-4 mt-4">
                <div><Label>Vendor *</Label><Select value={newCredit.vendor_id} onValueChange={(v) => setNewCredit({ ...newCredit, vendor_id: v })}><SelectTrigger><SelectValue placeholder="Select vendor" /></SelectTrigger><SelectContent>{vendors.map(v => <SelectItem key={v.vendor_id} value={v.vendor_id}>{v.company_name}</SelectItem>)}</SelectContent></Select></div>
                <div><Label>Date</Label><Input type="date" value={newCredit.credit_date} onChange={(e) => setNewCredit({ ...newCredit, credit_date: e.target.value })} /></div>
                <div><Label>Reason *</Label><Textarea value={newCredit.reason} onChange={(e) => setNewCredit({ ...newCredit, reason: e.target.value })} rows={2} placeholder="Reason for vendor credit" /></div>
                <div className="border rounded p-3 bg-slate-50">
                  <Label className="text-sm font-medium">Credit Amount</Label>
                  <div className="grid grid-cols-2 gap-2 mt-2">
                    <div><Label className="text-xs">Description</Label><Input value={newCredit.items[0].description} onChange={(e) => setNewCredit({ ...newCredit, items: [{ ...newCredit.items[0], description: e.target.value }] })} /></div>
                    <div><Label className="text-xs">Amount *</Label><Input type="number" min="0" value={newCredit.items[0].unit_price} onChange={(e) => setNewCredit({ ...newCredit, items: [{ ...newCredit.items[0], unit_price: parseFloat(e.target.value) || 0 }] })} /></div>
                  </div>
                </div>
              </div>
              <DialogFooter><Button variant="outline" onClick={() => setShowNew(false)}>Cancel</Button><Button onClick={createCredit} disabled={processing}>{processing ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : null}Create</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader><TableRow><TableHead>Credit #</TableHead><TableHead>Date</TableHead><TableHead>Vendor</TableHead><TableHead>Reason</TableHead><TableHead className="text-right">Amount</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
          <TableBody>
            {credits.map((c) => (
              <TableRow key={c.credit_id}>
                <TableCell className="font-medium">{c.credit_number}</TableCell>
                <TableCell>{c.credit_date}</TableCell>
                <TableCell>{c.vendor_name}</TableCell>
                <TableCell className="max-w-xs truncate">{c.reason}</TableCell>
                <TableCell className="text-right text-green-600">{formatCurrency(c.total_amount)}</TableCell>
                <TableCell><Badge variant={c.status === 'applied' ? 'default' : 'outline'}>{c.status}</Badge></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {credits.length === 0 && <div className="text-center py-8 text-slate-500">No vendor credits</div>}
      </CardContent>
    </Card>
  );
}
